/**
 * MenuSelect and MenuMultiSelect (DESIGN.md §5 Inputs: Select): a button that shows the current choice and opens a
 * DropdownMenu with radio items (one value) or checkbox items (several values). Keyboard and screen reader support
 * come from the Radix menu; the trigger is labelled by `label` (visible text or aria-label of the caller).
 * Used where a choice has more options than a SegmentedControl fits (rule editor: predicates, detected types).
 */
import { ChevronDownIcon } from 'lucide-react';
import type { AriaAttributes } from 'react';
import { cn } from '../lib/utils';
import { Button } from './button';
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from './dropdown-menu';

export interface MenuOption<T extends string> {
  value: T;
  label: string;
}

interface MenuSelectBase extends Pick<AriaAttributes, 'aria-invalid' | 'aria-describedby'> {
  /** Accessible name of the trigger (e.g. the field label). */
  label: string;
  disabled?: boolean;
  className?: string;
}

export interface MenuSelectProps<T extends string> extends MenuSelectBase {
  value: T;
  options: readonly MenuOption<T>[];
  onChange: (value: T) => void;
}

/** Single choice from a menu. */
export function MenuSelect<T extends string>({ label, value, options, onChange, disabled, className, ...validation }: MenuSelectProps<T>) {
  const current = options.find((option) => option.value === value)?.label ?? value;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button type="button" variant="outline" aria-label={`${label}: ${current}`} {...validation} className={cn('justify-between font-normal', className)}>
          <span className="truncate">{current}</span>
          <ChevronDownIcon aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuRadioGroup value={value} onValueChange={(next) => onChange(next as T)}>
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export interface MenuMultiSelectProps<T extends string> extends MenuSelectBase {
  values: readonly T[];
  options: readonly MenuOption<T>[];
  onChange: (values: T[]) => void;
  /** Trigger text while nothing is selected. */
  placeholder: string;
}

/** Several choices from a menu (the menu stays open while ticking). The trigger lists the chosen labels. */
export function MenuMultiSelect<T extends string>({ label, values, options, onChange, placeholder, disabled, className, ...validation }: MenuMultiSelectProps<T>) {
  const chosen = options.filter((option) => values.includes(option.value)).map((option) => option.label);
  const text = chosen.length > 0 ? chosen.join(', ') : placeholder;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button type="button" variant="outline" aria-label={`${label}: ${text}`} {...validation} className={cn('justify-between font-normal', className)}>
          <span className={cn('truncate', chosen.length === 0 && 'text-muted-foreground')}>{text}</span>
          <ChevronDownIcon aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={values.includes(option.value)}
            onSelect={(event) => event.preventDefault()}
            onCheckedChange={(checked) => onChange(checked ? [...values, option.value] : values.filter((v) => v !== option.value))}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
